import { Group, SelectProps, Stack } from '@mantine/core';
import take from 'lodash/take';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { ResetButton } from 'shared/components/buttons/ResetButton/ResetButton';
import { Select } from 'shared/components/selects/Select';

import { ColorInputPanel } from '../../../../../../components/controlledPanels/ColorInputPanel/ColorInputPanel';
import { FormControl } from '../../../../../../components/controlledPanels/FormControl/FormControl';
import { SwitchPanel } from '../../../../../../components/controlledPanels/SwitchPanel/SwitchPanel';
import { DEFAULT_IS_SHADOW_VISIBLE, DEFAULT_SHADOW, TOOLTIP_SHADOWS } from '../../consts';
import { extractShadowColor } from '../../utils/tooltipUtils';
import type { TooltipConfigProps } from '../TooltipConfig';

export function Shadow(props: TooltipConfigProps) {
  const { styles, onRemoveStyle, onUpdateStyle } = props;
  const { t } = useTranslation('components.theme.settings');
  const computedStyle = styles.shadow ?? DEFAULT_SHADOW;
  const shadowSettings = take(computedStyle?.split(' '), 4);
  const shadowColor = extractShadowColor(computedStyle);

  const setShadow = useCallback(
    (newShadow: string) => {
      onUpdateStyle({ shadow: newShadow });
    },
    [onUpdateStyle]
  );

  const setShadowSettings = useCallback(
    (newSettings: string) => {
      setShadow(`${newSettings} ${shadowColor}`);
    },
    [setShadow, shadowColor]
  );

  const setShadowColor = useCallback(
    (newColor: string) => {
      setShadow(`${shadowSettings.join(' ')} ${newColor}`);
    },
    [setShadow, shadowSettings]
  );

  return (
    <FormControl title={t('shadow')}>
      <Stack>
        <SwitchPanel
          value={styles.isShadowVisible ?? DEFAULT_IS_SHADOW_VISIBLE}
          onChange={isShadowVisible => onUpdateStyle({ isShadowVisible })}
          ResetButton={'isShadowVisible' in styles && <ResetButton onClick={() => onRemoveStyle('isShadowVisible')} />}
        />
        <Group>
          <Select
            label={t('style')}
            data={TOOLTIP_SHADOWS}
            value={shadowSettings.join(' ')}
            onChange={setShadowSettings as SelectProps['onChange']}
          />
          <ColorInputPanel label={t('color')} color={shadowColor} setColor={setShadowColor} />
          {styles.shadow && <ResetButton onClick={() => onRemoveStyle('shadow')} />}
        </Group>
      </Stack>
    </FormControl>
  );
}
